import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { hasAccess } from "../../../Home/Components/Main";
import { NotificationType } from "../Documents/Services/inventory-services/inventoryServicesTypes";
import {
  Breadcrumb,
  Button,
  Input,
  Layout,
  notification,
  Pagination,
  Popconfirm,
  Popover,
  Space,
  Table,
  Tooltip,
} from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import { getAllGateEntries, updateGateEntry } from "../../../redux/actions/API/gateEntry";
import { TableProps } from "antd/lib";
import { DataType } from "../IProfileSeries";
import { TableRowSelection } from "antd/es/table/interface";
import noResult from "../../../Utility/images/noresult.png";
import { downloadExcel } from "../../../Utility/downloadExcel";
import Loader from "../../../Home/Loader/Loader";
import { Content } from "antd/es/layout/layout";
import {
  PlusOutlined,
  DownloadOutlined,
  FilterOutlined,
  SearchOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import ColumnWidths from "../../utility/columnWidth";
import moment from "moment";
import FilterMenu from "../../../Home/Components/filter/FilterMenu";
import "./GateEntries.css";

interface IGateEntriesProps {
  loginData: any;
  getAllGateEntries: Function;
  companyId: any;
  entriesData: any;
  getEntriesLoading: boolean;
  total: any;
  updateGateEntryState: any;
  updateGateEntryStatus: Function
}
const GateEntries: React.FC<IGateEntriesProps> = ({
  loginData,
  getAllGateEntries,
  companyId,
  entriesData,
  getEntriesLoading,
  total,
  updateGateEntryState,
  updateGateEntryStatus
}) => {
  const navigate = useNavigate();

  const [isMobileView, setIsMobileView] = useState(window.innerWidth <= 896);
  const [selectedRowKeys, setSelectedRowKeys] = useState<any>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [docNumber, setDocNumber] = useState<any>("");
  const [visitorName, setVisitorName] = useState<any>("");
  const [searchTerm, setSearchTerm] = useState<string>("");

  const [dateRange, setDateRange] = useState("");
  const [exitStatus, setExitStatus] = useState<number[]>([]);
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [filteredDocs, setFilteredDocs] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const prevUpdateStateRef = useRef<any>(null);
  const prevGetEntriesRef = useRef<boolean | null>(null);

  // Monitor filter application - when entries finish loading after filter change
  useEffect(() => {
    if (prevGetEntriesRef.current === true && !getEntriesLoading) {
      // Entries have finished loading after a filter was applied
      // You can add additional logic here if needed (e.g., scroll to top, notifications)
      setLoading(false)
    }
    prevGetEntriesRef.current = getEntriesLoading;
  }, [getEntriesLoading]);
  
  const filterMenuRef = useRef<any>(null);

  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const searchInput = useRef<any>(null);


  const getUserPermissions = () => {
    let permissionArray: {
      feature: any;
      subFeature: {
        name: any;
        create: any;
        view: any;
        edit: any;
        delete: any;
      }[];
    }[] = [];
    loginData?.data?.permissions.forEach((permission: any) => {
      const existingFeature = permissionArray.find(
        (item) => item.feature === permission.feature,
      );

      const subFeatureObj = {
        name: permission.subfeature,
        create: permission.create,
        view: permission.view,
        edit: permission.edit,
        delete: permission.delete,
      };

      if (existingFeature) {
        existingFeature.subFeature.push(subFeatureObj);
      } else {
        permissionArray.push({
          feature: permission.feature,
          subFeature: [subFeatureObj],
        });
      }
    });
    return permissionArray;
  };

  const getGateEntryCreatePermission = () => {
    return hasAccess(
      getUserPermissions(),
      "Gate Entry",
      "Gate Entry",
      "create",
      loginData?.data?.role,
    );
  };

  const openNotificationWithIcon = (
    type: NotificationType,
    message: string,
  ) => {
    notification.open({
      message: message,
      type: type,
      duration: 3,
    });
  };

  const getColumnSearchProps = (dataIndex: string) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }: any) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => {
            if (dataIndex === "documentNumber") {
              setDocNumber(selectedKeys[0] || "");
            }
            if (dataIndex === "visitorName") {
              setVisitorName(selectedKeys[0] || "");
            }
            confirm();
          }}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => {
              if (dataIndex === "documentNumber") {
                setDocNumber(selectedKeys[0] || "");
              }
              if (dataIndex === "visitorName") {
                setVisitorName(selectedKeys[0] || "");
              }
              confirm();
            }}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => {
              clearFilters?.();
              if (dataIndex === "documentNumber") {
                setDocNumber("");
              }
              if (dataIndex === "visitorName") {
                setVisitorName("");
              }
              confirm();
            }}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
    ),
    onFilter: (value: any, record: any) => {
      if (dataIndex === "securityName") {
        return record?.user?.name
          ?.toLowerCase()
          ?.includes?.(value?.toLowerCase?.());
      }
      if (dataIndex === "exitStatus") {
        const status = record?.status ? "Exited" : "Pending";
        return status?.toLowerCase()?.includes(value?.toLowerCase());
      }
      const rawValue = record?.[dataIndex];
      const comparableValue =
        rawValue === undefined || rawValue === null ? "" : String(rawValue);
      return comparableValue
        .toLowerCase()
        .includes((value as string).toLowerCase());
    },
    onFilterDropdownVisibleChange: (visible: boolean) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
  });

  const handleResize = () => {
    setIsMobileView(window.innerWidth <= 414);
  };

 const handleCloseEntry = (id : any) => {
      if (id) {
        setLoading(true);
        updateGateEntryStatus({
          id,
        });
      }
    };

      useEffect(() => {
        if (prevUpdateStateRef.current?.loading && !updateGateEntryState?.loading) {
          if (updateGateEntryState?.error) {
            console.error("Error closing gate entry:", updateGateEntryState.error);
           
          } else {
          
        getAllGateEntries({
      companyId: Number(companyId),
      documentNumber: docNumber ? docNumber?.split(" ") : "",
      visitorName: visitorName ? visitorName?.split(" ") : "",
      search: searchTerm ? searchTerm?.split(" ") : "",
      dateRange,
      exitStatus,
      pageSize,
      currentPage,
    });
          }
           setLoading(false);
        }
        prevUpdateStateRef.current = updateGateEntryState;
      }, [updateGateEntryState,   docNumber,
    visitorName,
    searchTerm,
    dateRange,
    exitStatus,
    currentPage,
    pageSize,]);

  const columns: TableProps<any>["columns"] = [
    {
      title: "Document Number",
      key: "documentNumber",
      dataIndex: "documentNumber",
      width: ColumnWidths.DEFAULT,
      fixed: isMobileView ? undefined : "left",
      render: (text: string, record: any) => (
        <span
          className="link"
          onClick={() =>
            navigate("/previewGateEntry?documentNumber=" + record?.id)
          }
        >
          {text}
        </span>
      ),
      ...getColumnSearchProps("documentNumber"),
    },
    {
      title: "Entry Time",
      key: "createdAt",
      dataIndex: "createdAt",
      width: ColumnWidths.DEFAULT,
      render: (text: string) => {
        const date = moment.utc(text);
        if (!text || !date.isValid()) {
          return "N/A";
        }
        return `${date.utcOffset("+05:30").format("DD-MM-YYYY")} ${date
          .utcOffset("+05:30")
          .format("h:mm A")}`;
      },
    },
    {
      title: "Visitor Name",
      key: "visitorName",
      dataIndex: "visitorName",
      width: ColumnWidths.DEFAULT,
      render: (text: string) => text || "N/A",
      ...getColumnSearchProps("visitorName"),
    },
    {
      title: "Mobile Number",
      key: "visitorContact",
      dataIndex: "visitorContact",
      width: ColumnWidths.DEFAULT,
      render: (text: string) => text || "N/A",
    },
    {
      title: "Email",
      key: "visitorEmail",
      dataIndex: "visitorEmail",
      width: 300,
      render: (text: string) => text || "N/A",
    },
    {
      title: "Company Name",
      key: "visitorCompany",
      dataIndex: "visitorCompany",
      width: ColumnWidths.DEFAULT,
      render: (text: string) => text || "N/A",
    },
    {
      title: "ID Proof Type",
      key: "idProofType",
      dataIndex: "idProofType",
      width: ColumnWidths.DEFAULT,
      render: (text: string) => text || "N/A",
    },
    {
      title: "ID Number",
      key: "idProofNumber",
      dataIndex: "idProofNumber",
      width: ColumnWidths.DEFAULT,
      render: (text: string) => text || "N/A",
    },
    {
      title: "Purpose of Visit",
      key: "purposeOfVisit",
      dataIndex: "purposeOfVisit",
      width: ColumnWidths.DEFAULT,
      render: (text: string) => text || "N/A",
    },
    {
      title: "Person To Meet",
      key: "personToMeet",
      dataIndex: "personToMeet",
      width: ColumnWidths.DEFAULT,
      render: (text: string) => text || "N/A",
    },
    {
      title: "Vehicle Type",
      key: "vehicleType",
      dataIndex: "vehicleType",
      width: ColumnWidths.DEFAULT,
      render: (text: string) => text || "N/A",
    },
    {
      title: "Vehicle Number",
      key: "vehicleNumber",
      dataIndex: "vehicleNumber",
      width: ColumnWidths.DEFAULT,
      render: (text: string) => text || "N/A",
    },
    {
      title: "Comment",
      key: "comments",
      dataIndex: "comments",
      width: ColumnWidths.DEFAULT,
      render: (text: string) => text || "N/A",
    },
    {
      title: "Exit Status",
      key: "status",
      dataIndex: "status",
      width: ColumnWidths.DEFAULT,
      render: (text: string, record: any) => (
        <div>
          {text ? "Exited" : "Pending"}
          {!text && (
            <Tooltip title="Close Entry">
              <Popconfirm
                title="Are you sure to close this entry?"
                onConfirm={() => handleCloseEntry(record?.id)}
                okText="Yes"
                cancelText="No"
              >
                <CheckCircleOutlined
                  style={{
                    fontSize: 16,
                    cursor: "pointer",
                    paddingLeft: 10,
                    color: "#1677ff",
                  }}
                />
              </Popconfirm>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: "Exit Time",
      key: "updatedAt",
      dataIndex: "updatedAt",
      width: ColumnWidths.DEFAULT,
      render: (text: string, record: any) => {
        const date = moment.utc(text);
        if (!text || !date.isValid()) {
          return "N/A";
        }
        return record?.status === 1
          ? `${date.utcOffset("+05:30").format("DD-MM-YYYY")} ${date
              .utcOffset("+05:30")
              .format("h:mm A")}`
          : "N/A";
      },
    },
    {
      title: "Created By",
      key: "securityName",
      dataIndex: "securityName",
      width: ColumnWidths.DEFAULT,
      render: (text: string, record: any) =>
        record?.user?.name || text || "N/A",
    },
  ];

  const handleFilteredData = (filteredData: any[]) => {
    setFilteredDocs(filteredData);
  };

  const handleChange = (e: any) => {
    const value = e.target.value;
    setSearch(value);
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      setSearchTerm(value.trim());
    }, 1000);
  };

  useEffect(() => {
    getAllGateEntries({
      companyId: Number(companyId),
      documentNumber: docNumber ? docNumber?.split(" ") : "",
      visitorName: visitorName ? visitorName?.split(" ") : "",
      search: searchTerm ? searchTerm?.split(" ") : "",
      dateRange,
      exitStatus,
      pageSize,
      currentPage,
    });
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [
    docNumber,
    visitorName,
    searchTerm,
    dateRange,
    exitStatus,
    currentPage,
    pageSize,
  ]);

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection: TableRowSelection<DataType> = {
    selectedRowKeys,
    onChange: onSelectChange,
    type: "checkbox",
  };

  const renderGateEntriesTable = (): JSX.Element => {
    if (
      !hasAccess(
        getUserPermissions(),
        "Gate Entry",
        "Gate Entry",
        "view",
        loginData?.data?.role,
      )
    ) {
      return (
        <div style={{ textAlign: "center", marginTop: "10%" }}>
          <p>You don't have permission to view Gate Entries.</p>
        </div>
      );
    }

    if (entriesData && entriesData.length > 0) {
      return (
        <>
          <Table
            columns={columns}
            rowKey="documentNumber"
            dataSource={entriesData}
            bordered
            pagination={false}
            scroll={{ x: "100%", y: "calc(100vh - 320px)" }}
            rowSelection={rowSelection}
          />
          <Pagination
            total={total}
            defaultPageSize={20}
            current={currentPage}
            pageSize={pageSize}
            onChange={(page, pageSize) => {
              setCurrentPage(page);
              setPageSize(pageSize);
            }}
            showSizeChanger
            style={{ textAlign: "center", marginTop: 20 }}
          />
        </>
      );
    }

    return (
      <div className="noData">
        <img src={noResult} alt="documentsData" />
        <p className="mt-20">
          No Documents found. Please create a document
        </p>
      </div>
    );
  };

  const downloadSelectedRow = () => {
    const formatGateEntryDateTime = (value: string) => {
      const date = moment.utc(value);
      if (!value || !date.isValid()) {
        return "N/A";
      }
      return `${date.utcOffset("+05:30").format("DD-MM-YYYY")} ${date
        .utcOffset("+05:30")
        .format("h:mm A")}`;
    };

    const selectedData = entriesData
      ?.filter((doc: any) =>
        selectedRowKeys?.map(String).includes(String(doc?.documentNumber)),
      )
      ?.map((doc: any) => ({
        "Document Number": doc?.documentNumber || "",
        "Entry Time": formatGateEntryDateTime(doc?.createdAt),
        "Visitor Name": doc?.visitorName || "",
        "Mobile Number": doc?.visitorContact || "",
        Email: doc?.visitorEmail || "",
        "Company Name": doc?.visitorCompany || "",
        "ID Proof Type": doc?.idProofType || "",
        "ID Number": doc?.idProofNumber || "",
        "Purpose of Visit": doc?.purposeOfVisit || "",
        "Person To Meet": doc?.personToMeet || "",
        "Vehicle Type": doc?.vehicleType || "",
        "Vehicle Number": doc?.vehicleNumber || "",
        Comment: doc?.comments || "",
        "Exit Status": doc?.status ? "Exited" : "Pending",
        "Exit Time": doc?.status ? formatGateEntryDateTime(doc?.updatedAt) : "",
        "Created By": doc?.user?.name || "",
      }));

    if (!selectedData?.length) {
      openNotificationWithIcon(
        "warning",
        "Please select at least one gate entry to download.",
      );
      return;
    }

    downloadExcel(selectedData, "Selected_Gate_Entries");
  };

  return (
    <>
      <Loader loading={getEntriesLoading || loading} />
      <Layout className="layout">
        <Breadcrumb
          items={[{ title: "Dashboard" }, { title: "Gate Entries" }]}
        />
        <Content className="content-section">
          <div className="mb-10">
            <div className="flexBox">
              <div>
                {getGateEntryCreatePermission() && (
                  <Space wrap>
                    <Button      onClick={() =>
            navigate("/create_gate_entry")
          } type="link">
                      <PlusOutlined /> Create Document
                    </Button>
                  </Space>
                )}
                {hasAccess(
                  getUserPermissions(),
                  "Gate Entry",
                  "Gate Entry",
                  "view",
                  loginData?.data?.role,
                ) && (
                  <Button
                    type="link"
                    onClick={downloadSelectedRow}
                    className="buttonAddItem"
                  >
                    <DownloadOutlined /> Download
                  </Button>
                )}
              </div>
              <div className="flexBox">
                {hasAccess(
                  getUserPermissions(),
                  "Gate Entry",
                  "Gate Entry",
                  "view",
                  loginData?.data?.role,
                ) && (
                  <div className="documentFilterSearchRow">
                    {!isMobileView ? (
                      <>
                        <Popover
                          styles={{ root: { width: 300 } }}
                          placement="bottom"
                          content={
                            <FilterMenu
                              ref={filterMenuRef}
                              filterType={"gateEntryFilter"}
                              filterData={entriesData || []}
                              onApplyFilter={(filteredData) => {
                                handleFilteredData(filteredData);
                                setShowFilterMenu(false);
                              }}
                              setExitStatus={setExitStatus}
                              setDateRange={setDateRange}
                              setLoading={setLoading}
                            />
                          }
                          trigger="click"
                          open={showFilterMenu}
                          onOpenChange={() => {
                            setShowFilterMenu(!showFilterMenu);
                          }}
                        >
                          <Button
                            className="buttonItemFilter"
                            type="link"
                            icon={<FilterOutlined />}
                          >
                            {isMobileView ? "" : "Filter"}
                          </Button>
                        </Popover>
                        <Input
                          placeholder="Search..."
                          style={{ width: 200 }}
                          value={search}
                          onChange={handleChange}
                          className="searchDocumentInput"
                        />
                      </>
                    ) : (
                      <>
                        <Input
                          placeholder="Search..."
                          style={{ width: 200 }}
                          value={search}
                          onChange={handleChange}
                          className="searchDocumentInput"
                        />
                        <Popover
                          styles={{
                            root: { width: 300 },
                          }}
                          placement="bottom"
                          content={
                            <FilterMenu
                              filterType={"gateEntryFilter"}
                              filterData={entriesData || []}
                              onApplyFilter={(filteredData) => {
                                handleFilteredData(filteredData);
                                setShowFilterMenu(false);
                              }}
                              setExitStatus={setExitStatus}
                              setDateRange={setDateRange}
                              setLoading={setLoading}
                            />
                          }
                          trigger="click"
                          open={showFilterMenu}
                          onOpenChange={() => {
                            setShowFilterMenu(!showFilterMenu);
                          }}
                        >
                          <Button
                            type="link"
                            icon={<FilterOutlined />}
                            className="buttonDocumentFilter"
                          >
                            {isMobileView ? "" : "Filter"}
                          </Button>
                        </Popover>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          {renderGateEntriesTable()}
        </Content>
      </Layout>
    </>
  );
};

const mapStateToProps = (state: any) => ({
  loginData: state.api.login,
  companyId: state.api.login?.data?.companyId,
  entriesData: state.api.getAllGateEntries?.data?.data,
  total: state.api.getAllGateEntries?.data?.total,
  getEntriesLoading: state.api.getAllGateEntries?.loading,
    updateGateEntryState: state.api.updateGateEntry,

});
const mapDispatchToProps = (dispatch: any) => ({
  getAllGateEntries: (payload: any) => dispatch(getAllGateEntries(payload)),
          updateGateEntryStatus: (payload: any) => dispatch(updateGateEntry(payload)),

});

export default connect(mapStateToProps, mapDispatchToProps)(GateEntries);
